<template>
  <div class="tile is-ancestor is-parent">
    <div class="tile is-parent is-vertical">
      <article class="tile is-child box">
        <div class="columns is-gapless">
          <div class="column">
            <p class="title">Misc</p>
            <subtitle-totals :labels="['Total']"
              :totals="[miscsTotal]"
              :shows="[true]" />
          </div>
          <div v-if="!readOnlyView"
            class="column">
            <div class="control is-pulled-right">
              <div v-if="!isLockPurchaseOrder"
                class="buttons">
                <button class="button is-primary tooltip is-tooltip-topright"
                  :disabled="isLockPurchaseOrder"
                  data-tooltip="Add new misc"
                  @click="toggleQuotedMiscs()">
                  <span class="icon">
                    <i class="mdi"
                      :class="{ 'mdi-plus' : !isQuotedMiscsActive, 'mdi-minus' : isQuotedMiscsActive }" />
                  </span>
                  <span>{{ isQuotedMiscsActive ? 'Close' : 'Add' }}</span>
                </button>
              </div>
              <div v-else>
                <order-lock-notification />
              </div>
            </div>
          </div>
        </div>
        <quoted-miscs v-if="isQuotedMiscsActive"
          v-model="innerValue.miscs"
          :quote-id="innerValue.quoteId"
          :purchase-order-id="innerValue.purchaseOrderId"
          :read-only-view="readOnlyView"
          @item-added="onItemAdded"
          @item-deleted="deleteAllItems" />
        <div v-if="isQuotedMiscsActive"
          class="is-divider" />
        <bulma-table class="table is-bordered is-striped is-narrow is-fullwidth"
          :columns="columns"
          :page-index="1"
          :page-size="100"
          :total-rows="activeItems.length"
          :show-pagination="false"
          :is-loading="isTableLoading"
          :sort-column="sortColumn"
          :sort-order="sortOrder"
          @sort="sort">
          <tr v-for="(item, index) in activeItems"
            :key="item.purchaseOrderItemId"
            :class="{ 'is-selected' : selectedRow === index }">
            <td class="itemno-input has-vertical-middle">
              {{ item.itemNo }}
              <span v-if="item.quoteItemDeleted"
                class="icon has-text-danger tooltip is-tooltip-warning"
                data-tooltip="Item has been deleted in quote">
                <i class="mdi mdi-alert-circle mdi-18px" />
              </span>
              <span v-if="(item.quantity + item.otherPoOrderedQty) > item.quotedItemQty"
                style="padding-right: 3px"
                class="icon has-text-warning tooltip is-tooltip-warning"
                data-tooltip="This item has been ordered in other Purchase Order">
                <i class="mdi mdi-alert mdi-18px" />
              </span>
            </td>
            <td class="has-vertical-middle itemdesc-input">
              <input v-if="!readOnlyView"
                class="input"
                :disabled="isLockPurchaseOrder"
                placeholder="Item Description"
                v-model="item.description">
              <span v-else>{{ item.description }}</span>
            </td>
            <td class="has-vertical-middle has-text-right money-input">
              <vue-numeric v-if="!readOnlyView"
                class="input has-text-right"
                v-model.number="item.dealerCost"
                :min="0"
                :disabled="isLockPurchaseOrder"
                :precision="2" />
              <span v-else>{{ $filters.formatNumber(item.dealerCost, $userInfo.locale) }}</span>
            </td>
            <td class="has-vertical-middle has-text-right">{{ item.receivedQty - item.returnQty }}</td>
            <td class="has-vertical-middle has-text-centered is-content-width">
              <button class="button is-danger is-small is-inverted"
                @click="deleteItem(item, index)"
                :disabled="readOnlyView || isLockPurchaseOrder">
                <span class="icon is-medium">
                  <i class="mdi mdi-delete mdi-24px" />
                </span>
              </button>
            </td>
          </tr>
          <template slot="empty">
            <section class="section">
              <div class="content has-text-grey has-text-centered">
                <span icon="icon is-large">
                  <i class="mdi mdi-48px mdi-emoticon-sad" />
                </span>
                <p>Nothing</p>
              </div>
            </section>
          </template>
          <template slot="footer">
            <tr class="no-border"
              :class="[ miscsTotal >= 0 ? `purchase-order-background-success-${$theme.name}` : `purchase-order-background-danger-${$theme.name}` ]">
              <th class="has-text-left"
                :colspan="totalColSpan">Totals</th>
              <th class="has-text-right">{{ $filters.formatCurrency(miscsTotal, $userInfo.locale) }}</th>
              <th :colspan="blankColSpan" />
            </tr>
          </template>
        </bulma-table>
      </article>
    </div>
  </div>
</template>

<script>
import _cloneDeep from 'lodash/cloneDeep'
import _orderBy from 'lodash/orderBy'
import BulmaTable from '@/components/BulmaTable'
import QuotedMiscs from './components/QuotedMiscs'
import VueNumeric from '@/components/VueNumeric'
import SubtitleTotals from '@/components/SubtitleTotals'
import { EventHubTypes } from '@/enums'
import { ItemsToOrderColumns } from './columns'
import { roundAwayFromZero } from '@/components/utils/AccountingFunctions'
import OrderLockNotification from './components/PurchaseOrderLockNotification'

const title = 'Misc'

export default {
  name: 'PurchaseOrderMisc',
  components: {
    BulmaTable,
    QuotedMiscs,
    VueNumeric,
    SubtitleTotals,
    OrderLockNotification
  },
  props: {
    value: null,
    readOnlyView: {
      type: Boolean
    },
    isReceive: {
      type: Boolean,
      default: false
    },
    isLockPurchaseOrder: {
      type: Boolean
    }
  },
  data() {
    return {
      innerValue: _cloneDeep(this.value),
      quotedItem: null,
      itemToOrder: null,
      isTableLoading: false,
      selectedRow: false,
      isQuotedMiscsActive: false,
      sortColumn: '',
      sortOrder: ''
    }
  },
  computed: {
    columns() {
      return ItemsToOrderColumns
    },
    activeItems() {
      return this.innerValue.miscs.filter((i) => !i.deleted)
    },
    miscsTotal() {
      return roundAwayFromZero(
        this.activeItems.reduce(function (total, item) {
          return total + item.dealerCost
        }, 0),
        2
      )
    },
    totalColSpan() {
      // return this.columns.length - (this.$userInfo.isSupportUser ? 3 : 4)
      return this.columns.length - 3
    },
    blankColSpan() {
      return this.columns.length - this.totalColSpan - 1
    }
  },
  watch: {
    innerValue: {
      handler: function (newVal, oldVal) {
        this.$emit('input', newVal)
      },
      deep: true
    }
  },
  beforeDestroy() {
    this.$eventHub.$off(EventHubTypes.EntitySaved)
    this.$eventHub.$off(EventHubTypes.EntityReload)
  },
  created() {},
  mounted() {
    this.$eventHub.$on(EventHubTypes.EntitySaved, () => {
      this.innerValue = _cloneDeep(this.value)
      this.isQuotedMiscsActive = false
    })
    this.$eventHub.$on(EventHubTypes.EntityReload, () => {
      this.innerValue = _cloneDeep(this.value)
      this.isQuotedMiscsActive = false
    })
  },
  methods: {
    toggleQuotedMiscs() {
      this.isQuotedMiscsActive = !this.isQuotedMiscsActive
    },
    onItemAdded(newItem) {
      this.innerValue.miscs.splice(this.innerValue.miscs.length, 1, newItem)
    },
    deleteItem(item, index) {
      if (item.isNew) {
        const itemIndex = this.innerValue.miscs
          .map(function (obj) {
            return obj.purchaseOrderItemId
          })
          .indexOf(item.purchaseOrderItemId)
        if (itemIndex >= 0) {
          this.innerValue.miscs.splice(itemIndex, 1)
        }
      } else {
        let netRecQty = item.receivedQty - item.returnQty
        if (netRecQty > 0 || item.returnQty > 0) {
          this.$notification.openNotificationWithType('danger', title, 'This item has been received or returned. You can\'t delete it.')
          return false
        }
        item.deleted = true
        item.isDeleted = true
      }
    },
    deleteAllItems() {
      for (var i = 0; i < this.innerValue.miscs.length; i++) {
        const itemIndex = this.innerValue.miscs
          .map(function(obj) {
            return obj.purchaseOrderItemId
          })
          .indexOf(this.innerValue.miscs[i].purchaseOrderItemId)
        if (itemIndex >= 0) {
          if ((this.innerValue.miscs[itemIndex].receivedQty - this.innerValue.miscs[itemIndex].returnQty <= 0) && this.innerValue.miscs[itemIndex].returnQty <= 0) {
            this.innerValue.miscs[itemIndex].isDeleted = true
            this.innerValue.miscs[itemIndex].deleted = true
          }
        }
      }

      var items = this.innerValue.miscs
      items.reduce(function(list, item, index) {
      if (item.isDeleted && item.isNew) list.push(index)
        return list
      }, []).reverse().forEach(function(index) {
        items.splice(index,1)
      })

      this.innerValue.miscs = items
    },
    sort(name, order) {
      this.innerValue.miscs = _orderBy(this.activeItems, name, order)
    }
  }
}
</script>

<style lang="scss" scoped>
.itemno-input {
  min-width: 2.5em;
}
.itemdesc-input {
  min-width: 20em;
}
.money-input {
  max-width: 50px;
}
</style>